import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { trackExitIntent } from '../lib/analytics';

const STORAGE_KEY = 'farmfreshlily_exit_intent_shown';
const MIN_DWELL_MS = 12000; // don't fire in the first 12s
const HIDE_ROUTES = ['/confirmation', '/checkout', '/admin', '/track-order'];

const OPTIONS = [
  { key: 'price', label: 'Price felt too high' },
  { key: 'browsing', label: 'Just browsing today' },
  { key: 'later', label: "I'll order later" },
  { key: 'other', label: 'Something else' },
];

export const ExitIntentPopup = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const shownRef = useRef(false);
  const mountedAt = useRef(Date.now());

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (sessionStorage.getItem(STORAGE_KEY) === '1') {
      shownRef.current = true;
      return undefined;
    }

    const shouldSuppress = () =>
      HIDE_ROUTES.some(r => location.pathname.startsWith(r)) ||
      shownRef.current ||
      Date.now() - mountedAt.current < MIN_DWELL_MS;

    const trigger = () => {
      if (shouldSuppress()) return;
      shownRef.current = true;
      sessionStorage.setItem(STORAGE_KEY, '1');
      setOpen(true);
    };

    // Desktop: mouse leaves through the top of viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };

    // Mobile: quick upward flick suggests intent to close tab / switch app
    let lastY = null;
    let lastT = null;
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!t) return;
      if (lastY !== null && lastT !== null) {
        const dy = lastY - t.clientY;
        const dt = Date.now() - lastT;
        if (dy > 60 && dt < 180 && window.scrollY < 40) trigger();
      }
      lastY = t.clientY;
      lastT = Date.now();
    };

    document.addEventListener('mouseout', onMouseOut);
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [location.pathname]);

  const handlePick = (key) => {
    trackExitIntent(key);
    setSubmitted(true);
    setTimeout(() => setOpen(false), 1400);
  };

  const handleClose = () => {
    trackExitIntent('dismissed');
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 fade-in"
      style={{ background: 'rgba(45, 74, 45, 0.55)', backdropFilter: 'blur(6px)' }}
      data-testid="exit-intent-overlay"
      onClick={handleClose}
    >
      <div
        className="relative bg-brand-cream gold-border-glow max-w-md w-full rounded-sm p-8 sm:p-10"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-popup"
      >
        <button
          type="button"
          aria-label="Close"
          onClick={handleClose}
          className="absolute top-3 right-3 p-2 rounded-full text-brand-green-dark/60 hover:text-brand-green-dark smooth-transition"
          data-testid="exit-intent-close"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        {submitted ? (
          <div className="text-center py-6" data-testid="exit-intent-thanks">
            <p className="uppercase tracking-[0.3em] text-xs font-bold text-brand-gold mb-3">
              Thank You
            </p>
            <p className="font-heading text-2xl text-brand-green-dark">
              We appreciate the feedback.
            </p>
          </div>
        ) : (
          <>
            <p className="uppercase tracking-[0.3em] text-xs font-bold text-brand-gold mb-3 text-center">
              Before You Go
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl text-brand-green-dark text-center leading-tight mb-2">
              What stopped you from <span className="italic">ordering today?</span>
            </h2>
            <p className="font-body text-sm text-brand-green-dark/70 text-center mb-6">
              One tap. Helps us serve Hyderabad better.
            </p>

            <div className="space-y-2">
              {OPTIONS.map(opt => (
                <button
                  type="button"
                  key={opt.key}
                  onClick={() => handlePick(opt.key)}
                  className="w-full text-left px-5 py-3 border border-brand-gold/40 bg-brand-cream rounded-sm font-body text-sm text-brand-green-dark hover:bg-brand-green hover:text-brand-cream hover:border-brand-green smooth-transition"
                  data-testid={`exit-intent-option-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
